import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    document.addEventListener("click", function (event) {
      const detailsList = document.querySelectorAll("details[open]");

      detailsList.forEach(el => {
        if (!el.contains(event.target)) {
          el.removeAttribute("open");
        }
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

function initCartDrawerSlider() {
  if(document.querySelector("[js-cart-upsell-slider]")) {
    new Swiper('[js-cart-upsell-slider]', {
      slidesPerView: 'auto',
      spaceBetween: 24,
      loop: false,
      mousewheel: {
        forceToAxis: true, 
        sensitivity: 1, 
      }
    });
  }
  if(document.querySelector("[js-cart-free-gift-slider]")) {
    new Swiper('[js-cart-free-gift-slider]', {
      slidesPerView: 'auto',
      spaceBetween: 16,
      loop: false,
      mousewheel: {
        forceToAxis: true, 
        sensitivity: 1, 
      }
    });
  }
}

initCartDrawerSlider();
document.addEventListener("cart-drawer-slider:init", initCartDrawerSlider);

class CartFreeGift extends HTMLElement {
  constructor() {
    super();

    this.init();
    document.addEventListener("cart-free-gift:init", this.init.bind(this))
  }

  async init() {
    const cartFreeGift = document.querySelector("cart-free-gift");
    if(!cartFreeGift) return;

    const cartCount = parseInt(cartFreeGift.getAttribute('data-cart-count'));
    const giftKey = cartFreeGift.getAttribute('data-gift-key');
    const boxKey = cartFreeGift.getAttribute('data-box-key');
    this.sectionId = document.querySelector('cart-items-component')?.getAttribute('data-section-id');

    if(giftKey && cartCount === 0) {
      this.removeGift(giftKey)
    }

    if(boxKey && cartCount < 4) {
      this.removeGift(boxKey)
    }
  }

  async removeGift(id) {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id,
      quantity: 0,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.root + 'cart/change.js', fetchConfig('json', { body }));
    const responseText = await response.text();

    const parsedResponseText = JSON.parse(responseText);

    if(parsedResponseText.sections) {
      const newSectionHTML = new DOMParser().parseFromString(
        parsedResponseText.sections[this.sectionId],
        'text/html'
      );

      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          sections: parsedResponseText.sections,
        })
      );
    }
  }
}

customElements.define('cart-free-gift', CartFreeGift);